import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { money, financialYear } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Plus, FolderKanban, Pencil, Trash2, Check, Landmark } from "lucide-react";

const PALETTE = ["#0284C7", "#059669", "#D97706", "#E11D48", "#7C3AED", "#0891B2", "#CA8A04", "#113E35"];
const empty = () => ({ name: "", description: "", bank_account: "", color: PALETTE[0], active: true });

export default function ProjectsPage() {
  const [fy] = useState(financialYear());
  const [data, setData] = useState({ projects: [], unassigned: null });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = useCallback(async () => {
    const { data } = await api.get("/projects/summary", { params: { date_from: fy.from, date_to: fy.to } });
    setData(data);
  }, [fy]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, description: p.description || "", bank_account: p.bank_account || "", color: p.color || PALETTE[0], active: p.active }); setOpen(true); };

  const save = async () => {
    if (!form.name) return;
    if (editing) await api.put(`/projects/${editing.id}`, form);
    else await api.post("/projects", form);
    toast.success(editing ? "Project updated" : "Project added");
    setOpen(false);
    load();
  };
  const remove = async (id) => { await api.delete(`/projects/${id}`); toast.success("Project removed"); load(); };

  const Stat = ({ label, value, tone }) => (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">{label}</div>
      <div className={`num font-semibold ${tone || ""}`}>{value}</div>
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground text-sm mt-1">Track income, expenses & GST per project — map each to a NAB account. <span className="font-mono">{fy.label}</span></p>
        </div>
        <Button onClick={openNew} data-testid="add-project-btn" className="gap-2"><Plus className="w-4 h-4" /> Add project</Button>
      </div>

      {data.projects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card px-5 py-12 text-center" data-testid="projects-empty">
          <FolderKanban className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No projects yet. Add one (e.g. “Cafe”, “Website build”) and tag transactions to it.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.projects.map((p) => (
            <div key={p.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{ background: p.color }} />
                  <div className="min-w-0">
                    <div className="font-heading font-semibold truncate">{p.name}</div>
                    {p.bank_account && <div className="text-xs text-muted-foreground flex items-center gap-1 truncate"><Landmark className="w-3 h-3" /> {p.bank_account}</div>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(p)} data-testid={`edit-project-${p.id}`} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-project-${p.id}`} className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Stat label="Income" value={money(p.income)} tone="text-emerald-600" />
                <Stat label="Expenses" value={money(p.expense)} tone="text-rose-600" />
                <Stat label="Profit" value={money(p.profit)} tone={p.profit >= 0 ? "text-primary" : "text-rose-600"} />
                <Stat label="Net GST" value={money(p.net_gst)} tone="text-sky-700" />
              </div>
              <div className="mt-3 text-xs text-muted-foreground">{p.count} transaction{p.count === 1 ? "" : "s"}</div>
            </div>
          ))}
        </div>
      )}

      {data.unassigned && data.unassigned.count > 0 && (
        <div className="mt-4 rounded-2xl border border-border bg-secondary/40 p-5" data-testid="project-unassigned">
          <div className="font-heading font-semibold text-sm mb-3">Not assigned to a project</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Income" value={money(data.unassigned.income)} tone="text-emerald-600" />
            <Stat label="Expenses" value={money(data.unassigned.expense)} tone="text-rose-600" />
            <Stat label="Profit" value={money(data.unassigned.profit)} />
            <Stat label="Net GST" value={money(data.unassigned.net_gst)} tone="text-sky-700" />
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit project" : "New project"}</DialogTitle>
            <DialogDescription>Group income & expenses so you can see each project's own P&L.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5 py-1">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="project-name" placeholder="e.g. Website build" />
            </div>
            <div>
              <Label>NAB account <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} className="mt-1.5" data-testid="project-bank" placeholder="e.g. NAB •••• 1234" />
            </div>
            <div>
              <Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" data-testid="project-description" />
            </div>
            <div>
              <Label>Colour</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {PALETTE.map((col) => (
                  <button key={col} type="button" onClick={() => set("color", col)}
                    data-testid={`project-color-${col.replace("#", "")}`}
                    className="w-7 h-7 rounded-full grid place-items-center transition-transform hover:scale-110"
                    style={{ background: col, ...(form.color === col ? { boxShadow: `0 0 0 2px white, 0 0 0 4px ${col}` } : {}) }}>
                    {form.color === col && <Check className="w-3.5 h-3.5 text-white" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-project-btn">{editing ? "Save changes" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
